import { _ } from "js/vendor";
import React, { Component } from "reactn";
import linkDS, { LinkType } from "js/react/views/PresentationSettings/dataservices/LinkDataService";
import { BeautifulDialog, DialogContent, ShowDialog, ShowDialogAsync, ShowErrorDialog } from "js/react/components/Dialogs/BaseDialog";
import { FlexSpacer, Gap10, Gap20 } from "js/react/components/Gap";
import Spinner from "js/react/components/Spinner";
import { app } from "js/namespaces";
import { trackActivity } from "js/core/utilities/utilities";
import { InvitePreviewEmailDialog } from "./ShareDialog";
import TextArea from "js/react/components/TextArea";
import { DialogTitle, DialogActions, Button } from "@material-ui/core";
import { ToggleAllowCommenting } from "../components/ToggleAllowCommenting";
import LinkExpiration from "js/react/components/LinkExpiration";
import { createInviteSuggestionContext } from "js/react/components/InviteInput/InviteContext";
import { InviteTagsInput } from "js/react/components/InviteInput/InviteTagsInput";
import { ConfirmInviteDialog } from "js/react/components/InviteInput/ConfirmInviteDialog";
import permissionsDS from "../dataservices/PermissionsDataService";

export default class CreateInviteDialog extends Component {
    state = {
        inviteContext: null,
        showSpinner: false,
        customMessage: "",
        allowCommenting: false,
        linkExpiresAt: null,
        linkShouldExpire: false,
        linkHasExpired: false,
    };

    static get type() {
        return "CreateInvite";
    }

    componentDidMount = async () => {
        const presentation = this.global.presentation;

        let links = _.filter(presentation.links.models, link => link.get("type") == LinkType.INVITE);
        let oldSuggestions = links.map(link => link.get("email"));

        let inviteContext = createInviteSuggestionContext({
            includeFolders: false,
            allowOldSelection: true,
            presentationId: presentation.id,
            oldSuggestions,
            saveContext: inviteContext => this.setState({ inviteContext }),
        });

        this.setState({
            inviteContext,
        });
    }

    componentWillUnmount() {
        this.state.inviteContext.destroy();
    }

    handleAccept = async () => {
        let {
            inviteContext,
            customMessage,
            allowCommenting,
            linkExpiresAt,
        } = this.state;
        const presentation = this.global.presentation;

        try {
            this.setState({ showSpinner: true });

            // We're calling getSelectionWithRedundanciesRemoved() instead of getSelectedUniqueUsers() because
            //   we are not suggesting folders and we want to to ensure non-redundant invitations
            let users = inviteContext.getSelectionWithRedundanciesRemoved();

            await ShowDialogAsync(ConfirmInviteDialog, {
                title: "Invite the following viewers?",
                includeSubheader: false,
                hasPermissionEditing: false,
                users,
                callback: async permission => {
                    let emails = users.map(user => user.email);

                    let asyncInvites = emails.map(email => {
                        return linkDS.createInviteLink(
                            presentation,
                            email,
                            customMessage,
                            linkExpiresAt,
                            allowCommenting
                        )
                            .then(link => {
                                return linkDS
                                    .saveLink(presentation, link)
                                    .then(() => link.id);
                            });
                    });

                    let linkIds = await Promise.all(asyncInvites);
                    await permissionsDS.fetchPermissions();

                    this.setGlobal({
                        alertMessage: "Invites were sent"
                    });

                    const props = {
                        presentation_id: presentation.id,
                        link_ids: linkIds,
                        is_new: true,
                        type: "email",
                        sender_email: app.user.getEmail(),
                        recipient_emails: emails,
                        workspace_id: presentation.getWorkspaceId()
                    };
                    trackActivity("Sharing", "EmailLink", null, null, props, { audit: true });

                    // Close next frame to avoid React errors
                    setTimeout(() => {
                        this.props.closeDialog();
                        this.props.callback();
                    });
                },
            });
        } catch (err) {
            ShowErrorDialog({
                error: "Error while sharing",
                message: err.message
            });
            console.error(err);
        } finally {
            this.setState({ showSpinner: false });
        }
    };

    handleUpdateLinkExpiration = ({
        linkExpiresAt,
        linkShouldExpire,
        linkHasExpired,
    }) => {
        this.setState({
            linkExpiresAt,
            linkShouldExpire,
            linkHasExpired,
        });
    }

    handleChangeCustomMessage = customMessage => {
        this.setState({
            customMessage
        });
    };

    handlePreviewEmail = async () => {
        ShowDialog(InvitePreviewEmailDialog, {
            presentation: this.props.presentation,
            customMessage: this.state.customMessage
        });
    };

    toggleAllowCommenting = event => {
        const allowCommenting = !this.state.allowCommenting;
        this.setState({ allowCommenting });
        this.forceUpdate();
    }

    render() {
        const {
            inviteContext,
            showSpinner,
            customMessage,
            allowCommenting,
            linkExpiresAt,
            linkShouldExpire,
            linkHasExpired,
        } = this.state;
        const { presentation } = this.props;
        const isSendDisabled = (
            !inviteContext?.selection.length ||
            (
                linkShouldExpire &&
                linkHasExpired
            )
        );

        return (
            <BeautifulDialog
                maxWidth="md"
                closeDialog={this.props.closeDialog}
            >
                {showSpinner && <Spinner />}
                <DialogTitle>
                    Invite People to View this Presentation
                </DialogTitle>
                <DialogContent>
                    <h3>
                        We’ll send each person an email with an individual link to view this presentation.
                        Pro users can track engagement with comments and viewer analytics.
                    </h3>
                    <Gap10 />
                    {
                        inviteContext &&
                        <InviteTagsInput
                            inviteContext={inviteContext}
                            sourceName="CreateInviteDialog"
                        />
                    }
                    <Gap10/>
                    <TextArea
                        isVisible
                        text={customMessage}
                        onChange={customMessage => this.handleChangeCustomMessage(customMessage) }
                        placeholder="Add a note..."
                    />

                    <Gap20 />
                    <ToggleAllowCommenting
                        allowCommenting={allowCommenting}
                        onToggle={this.toggleAllowCommenting}
                    />
                    <LinkExpiration
                        expiresAt={linkExpiresAt}
                        onUpdate={this.handleUpdateLinkExpiration}
                        presentation={presentation}
                        stacked
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={this.handlePreviewEmail}>Preview Email</Button>
                    <FlexSpacer/>
                    <Button onClick={this.props.closeDialog}>
                        Cancel
                    </Button>
                    <Button disabled={isSendDisabled} onClick={this.handleAccept} color="primary">
                        Send Email
                    </Button>
                </DialogActions>
            </BeautifulDialog>
        );
    }
}
